import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { 
  Building2, 
  TrendingUp, 
  Settings, 
  Plus, 
  Trash2, 
  DollarSign,
  ArrowUpRight,
  ArrowDownRight,
  Pencil,
  Save,
  FolderOpen,
  Loader2,
  Copy,
  GitCompareArrows
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { LoanTypeFields, LOAN_TYPE_DEFAULTS, loanTypeFromApi, loanTypeToApi } from "@/components/LoanTypeFields";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { 
  LineChart, 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  Legend,
  Area,
  AreaChart,
  ReferenceLine
} from "recharts";
import { toast } from "sonner";
import { api, formatCurrency, formatPercent, getTaxCategory } from "@/lib/api";

export default function Forecasting() {
  const [properties, setProperties] = useState([]);
  const [forecast, setForecast] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isForecastLoading, setIsForecastLoading] = useState(false);
  
  // Forecast settings
  const [settings, setSettings] = useState({
    growth_rate: 5.0,
    rental_growth_rate: 3.0,
    expense_growth_rate: 2.5,
    interest_rate_forecast: 6.0,
    years: 40,
    selling_costs_percent: 2.5,
    cgt_estimate_percent: 23.5,
    ppor_value: 0,
    ppor_loan_balance: 0,
    ppor_interest_rate: null
  });
  
  // Scenario events (buy/sell)
  const [events, setEvents] = useState([]);
  const [showAddEventDialog, setShowAddEventDialog] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [newEvent, setNewEvent] = useState({
    year: 3,
    event_type: "buy",
    property_value: "",
    loan_amount: "",
    weekly_rent: "",
    interest_rate: "6.0",
    loan_term_years: "30",
    ...LOAN_TYPE_DEFAULTS,
    property_id: "",
    sale_price: "",
    proceeds_action: "cash",
    ppor_remainder_action: "cash"
  });

  // Saved scenarios
  const [savedScenarios, setSavedScenarios] = useState([]);
  const [currentScenarioId, setCurrentScenarioId] = useState(null);
  const [currentScenarioName, setCurrentScenarioName] = useState("");
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [saveAsNew, setSaveAsNew] = useState(true);
  const [saveName, setSaveName] = useState("");
  const [savingScenario, setSavingScenario] = useState(false);
  const [duplicating, setDuplicating] = useState(false);
  // Compare mode
  const [compareA, setCompareA] = useState("");
  const [compareB, setCompareB] = useState("");
  const [compareData, setCompareData] = useState(null);
  const [compareMeta, setCompareMeta] = useState(null);
  const [compareMetric, setCompareMetric] = useState("equity");
  const [comparing, setComparing] = useState(false);

  const fetchScenarios = async () => {
    try {
      const res = await api.get("/forecast/scenarios");
      setSavedScenarios(res.data.scenarios || []);
    } catch (e) {
      console.error("Error fetching scenarios:", e);
    }
  };

  useEffect(() => {
    fetchScenarios();
  }, []);

  const scenarioEventsForSave = () => events.map(e => ({
    year: parseInt(e.year) || 1,
    event_type: e.event_type,
    property_value: e.property_value !== "" && e.property_value != null ? parseFloat(e.property_value) : null,
    loan_amount: e.loan_amount !== "" && e.loan_amount != null ? parseFloat(e.loan_amount) : null,
    weekly_rent: e.weekly_rent !== "" && e.weekly_rent != null ? parseFloat(e.weekly_rent) : null,
    interest_rate: e.interest_rate !== "" && e.interest_rate != null ? parseFloat(e.interest_rate) : null,
    loan_term_years: e.loan_term_years !== "" && e.loan_term_years != null ? parseInt(e.loan_term_years) : 30,
    ...loanTypeToApi(e),
    property_id: e.property_id || null,
    sale_price: e.sale_price !== "" && e.sale_price != null ? parseFloat(e.sale_price) : null,
    proceeds_action: e.proceeds_action || "cash",
    ppor_remainder_action: e.ppor_remainder_action || "cash"
  }));

  const loadScenario = (scenarioId) => {
    const sc = savedScenarios.find(s => s.id === scenarioId);
    if (!sc) return;
    const loadedSettings = { ...settings, ...sc.settings };
    const loadedEvents = (sc.events || []).map((e, i) => ({
      id: Date.now() + i,
      year: e.year ?? 1,
      event_type: e.event_type || "buy",
      property_value: e.property_value != null ? String(e.property_value) : "",
      loan_amount: e.loan_amount != null ? String(e.loan_amount) : "",
      weekly_rent: e.weekly_rent != null ? String(e.weekly_rent) : "",
      interest_rate: e.interest_rate != null ? String(e.interest_rate) : "6.0",
      loan_term_years: e.loan_term_years != null ? String(e.loan_term_years) : "30",
      ...loanTypeFromApi(e),
      property_id: e.property_id || "",
      sale_price: e.sale_price != null ? String(e.sale_price) : "",
      proceeds_action: e.proceeds_action || "cash",
      ppor_remainder_action: e.ppor_remainder_action || "cash"
    }));
    setSettings(loadedSettings);
    setEvents(loadedEvents);
    setCurrentScenarioId(sc.id);
    setCurrentScenarioName(sc.name);
    runForecast(true, loadedEvents, loadedSettings);
    toast.success(`Loaded "${sc.name}"`);
  };

  const openSaveDialog = (asNew) => {
    setSaveAsNew(asNew);
    setSaveName(asNew ? "" : currentScenarioName);
    setShowSaveDialog(true);
  };

  const handleSaveScenario = async () => {
    const name = saveName.trim();
    if (!name) {
      toast.error("Please enter a scenario name");
      return;
    }
    setSavingScenario(true);
    try {
      const payload = { name, settings, events: scenarioEventsForSave() };
      let res;
      if (!saveAsNew && currentScenarioId) {
        res = await api.put(`/forecast/scenarios/${currentScenarioId}`, payload);
      } else {
        res = await api.post("/forecast/scenarios", payload);
      }
      setCurrentScenarioId(res.data.id);
      setCurrentScenarioName(res.data.name);
      setShowSaveDialog(false);
      await fetchScenarios();
      toast.success(`Scenario "${res.data.name}" saved`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save scenario");
    } finally {
      setSavingScenario(false);
    }
  };

  const handleDeleteScenario = async () => {
    if (!currentScenarioId) return;
    try {
      await api.delete(`/forecast/scenarios/${currentScenarioId}`);
      setCurrentScenarioId(null);
      setCurrentScenarioName("");
      await fetchScenarios();
      toast.success("Scenario deleted");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete scenario");
    }
  };

  const handleDuplicateScenario = async () => {
    if (!currentScenarioId) return;
    setDuplicating(true);
    try {
      const res = await api.post("/forecast/scenarios", {
        name: `${currentScenarioName} (copy)`.slice(0, 120),
        settings,
        events: scenarioEventsForSave(),
      });
      setCurrentScenarioId(res.data.id);
      setCurrentScenarioName(res.data.name);
      await fetchScenarios();
      toast.success(`Duplicated as "${res.data.name}" — rename and save your changes`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to duplicate scenario");
    } finally {
      setDuplicating(false);
    }
  };

  const forecastForScenario = async (sc) => {
    const evts = sc.events || [];
    if (evts.length > 0) {
      const res = await api.post("/forecast/scenario", { settings: sc.settings, events: evts });
      return res.data;
    }
    const res = await api.post("/forecast/portfolio", sc.settings);
    return res.data;
  };

  const runCompare = async () => {
    if (!compareA || !compareB) {
      toast.error("Pick two scenarios to compare");
      return;
    }
    if (compareA === compareB) {
      toast.error("Pick two different scenarios");
      return;
    }
    const scA = savedScenarios.find(s => s.id === compareA);
    const scB = savedScenarios.find(s => s.id === compareB);
    if (!scA || !scB) return;
    setComparing(true);
    try {
      const [ra, rb] = await Promise.all([forecastForScenario(scA), forecastForScenario(scB)]);
      const fa = ra.forecast || [];
      const fb = rb.forecast || [];
      const maxLen = Math.max(fa.length, fb.length);
      const merged = [];
      for (let i = 0; i < maxLen; i++) {
        merged.push({
          year: (fa[i] || fb[i]).year,
          a_value: fa[i]?.total_value ?? null,
          a_equity: fa[i]?.total_equity ?? null,
          a_cashflow: fa[i]?.net_cashflow ?? null,
          b_value: fb[i]?.total_value ?? null,
          b_equity: fb[i]?.total_equity ?? null,
          b_cashflow: fb[i]?.net_cashflow ?? null,
        });
      }
      setCompareData(merged);
      setCompareMeta({
        a: { name: scA.name, value: fa[fa.length - 1]?.total_value || 0, equity: fa[fa.length - 1]?.total_equity || 0, cashflow: fa[fa.length - 1]?.net_cashflow || 0 },
        b: { name: scB.name, value: fb[fb.length - 1]?.total_value || 0, equity: fb[fb.length - 1]?.total_equity || 0, cashflow: fb[fb.length - 1]?.net_cashflow || 0 },
      });
    } catch (error) {
      toast.error("Failed to compare scenarios");
    } finally {
      setComparing(false);
    }
  };

  useEffect(() => {
    const storedEvent = sessionStorage.getItem('forecastEvent');
    if (storedEvent) {
      try {
        const eventData = JSON.parse(storedEvent);
        // Add the event to our events list
        setEvents(prev => [...prev, {
          year: eventData.year || 1,
          event_type: "buy",
          property_value: eventData.property_value?.toString() || "",
          loan_amount: eventData.loan_amount?.toString() || "0",
          weekly_rent: eventData.weekly_rent?.toString() || "0",
          interest_rate: eventData.interest_rate?.toString() || "6.0",
          loan_term_years: eventData.loan_term_years?.toString() || "30",
          property_id: "",
          sale_price: "",
          from_analysis: true,
          analysis_address: eventData.analysis_address || ""
        }]);
        toast.success(`Property from analysis added: ${eventData.analysis_address || 'New property'}`);
        // Clear the stored event
        sessionStorage.removeItem('forecastEvent');
      } catch (e) {
        console.error("Error parsing forecast event:", e);
      }
    }
  }, []);

  useEffect(() => {
    const fetchProperties = async () => {
      try {
        const response = await api.get("/properties");
        setProperties(response.data);
      } catch (error) {
        console.error("Error fetching properties:", error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchProperties();
  }, []);

  const forecastRequestRef = useRef(0);
  const runForecast = async (useEvents = true, eventsOverride = null, settingsOverride = null) => {
    const evts = eventsOverride !== null ? eventsOverride : events;
    const stg = settingsOverride !== null ? settingsOverride : settings;
    const requestId = ++forecastRequestRef.current;
    setIsForecastLoading(true);
    try {
      // Use scenario endpoint if we have events and useEvents is true, otherwise use simple portfolio forecast
      if (evts.length > 0 && useEvents) {
        const response = await api.post("/forecast/scenario", {
          settings: stg,
          events: evts.map(e => ({
            year: e.year,
            event_type: e.event_type,
            property_value: e.event_type === "buy" ? parseFloat(e.property_value) : null,
            loan_amount: e.event_type === "buy" ? parseFloat(e.loan_amount) || 0 : null,
            weekly_rent: e.event_type === "buy" ? parseFloat(e.weekly_rent) || 0 : null,
            interest_rate: e.event_type === "buy" ? parseFloat(e.interest_rate) : null,
            loan_term_years: e.event_type === "buy" ? parseInt(e.loan_term_years) : null,
            ...(e.event_type === "buy" ? loanTypeToApi(e) : {}),
            property_id: e.event_type === "sell" ? e.property_id : null,
            sale_price: e.event_type === "sell" && e.sale_price ? parseFloat(e.sale_price) : null,
            proceeds_action: e.event_type === "sell" ? (e.proceeds_action || "cash") : null,
            ppor_remainder_action: e.event_type === "sell" ? (e.ppor_remainder_action || "cash") : null
          }))
        });
        if (requestId === forecastRequestRef.current) setForecast(response.data);
      } else {
        const response = await api.post("/forecast/portfolio", stg);
        if (requestId === forecastRequestRef.current) setForecast(response.data);
      }
    } catch (error) {
      console.error("Error running forecast:", error);
      if (requestId === forecastRequestRef.current) toast.error("Failed to run forecast");
    } finally {
      if (requestId === forecastRequestRef.current) setIsForecastLoading(false);
    }
  };

  // Auto-run forecast on initial load
  useEffect(() => {
    if (!isLoading) {
      runForecast();
    }
  }, [isLoading]);

  // Auto-run forecast when settings change (debounced)
  useEffect(() => {
    if (isLoading) return; // Initial load is handled above
    
    const timeoutId = setTimeout(() => {
      runForecast();
    }, 500); // 500ms debounce
    
    return () => clearTimeout(timeoutId);
  }, [settings, events]);

  const addEvent = () => {
    if (newEvent.event_type === "buy" && !newEvent.property_value) {
      toast.error("Please enter a property value");
      return;
    }
    if (newEvent.event_type === "sell" && !newEvent.property_id) {
      toast.error("Please select a property to sell");
      return;
    }
    
    if (editingEvent) {
      // Update existing event
      setEvents(events.map(e => e.id === editingEvent.id ? { ...newEvent, id: editingEvent.id } : e));
      setEditingEvent(null);
    } else {
      // Add new event
      setEvents([...events, { ...newEvent, id: Date.now() }]);
    }
    
    setShowAddEventDialog(false);
    setNewEvent({
      year: 3,
      event_type: "buy",
      property_value: "",
      loan_amount: "",
      weekly_rent: "",
      interest_rate: "6.0",
      loan_term_years: "30",
      ...LOAN_TYPE_DEFAULTS,
      property_id: "",
      sale_price: ""
    });
  };

  const editEvent = (event) => {
    setEditingEvent(event);
    setNewEvent({
      year: event.year,
      event_type: event.event_type,
      property_value: event.property_value || "",
      loan_amount: event.loan_amount || "",
      weekly_rent: event.weekly_rent || "",
      interest_rate: event.interest_rate || "6.0",
      loan_term_years: event.loan_term_years || "30",
      loan_type: event.loan_type || "pi",
      io_years_remaining: event.io_years_remaining ?? "5",
      io_indefinite: !!event.io_indefinite,
      property_id: event.property_id || "",
      sale_price: event.sale_price || "",
      proceeds_action: event.proceeds_action || "cash",
      ppor_remainder_action: event.ppor_remainder_action || "cash"
    });
    setShowAddEventDialog(true);
  };

  const removeEvent = (eventId) => {
    setEvents(events.filter(e => e.id !== eventId));
  };

  const closeEventDialog = () => {
    setShowAddEventDialog(false);
    setEditingEvent(null);
    setNewEvent({
      year: 3,
      event_type: "buy",
      property_value: "",
      loan_amount: "",
      weekly_rent: "",
      interest_rate: "6.0",
      loan_term_years: "30",
      ...LOAN_TYPE_DEFAULTS,
      property_id: "",
      sale_price: ""
    });
  };

  // Get event years for reference lines
  const eventYears = events.map(e => e.year);

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-12 w-64" />
        <Skeleton className="h-96" />
      </div>
    );
  }

  if (properties.length === 0) {
    return (
      <div className="text-center py-12" data-testid="forecasting-empty">
        <TrendingUp className="w-16 h-16 text-slate-300 mx-auto mb-4" />
        <h3 className="font-heading text-xl font-semibold text-slate-700 mb-2">
          No Properties Yet
        </h3>
        <p className="text-slate-500 mb-6">
          Add properties to forecast their growth
        </p>
        <Link to="/properties/new">
          <Button className="bg-[#547d91] hover:bg-[#456a7a] rounded-full">
            Add Property
          </Button>
        </Link>
      </div>
    );
  }

  const finalYear = forecast?.forecast?.[forecast.forecast.length - 1];

  return (
    <div className="space-y-8 animate-fade-in" data-testid="forecasting-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="font-heading text-2xl lg:text-3xl font-bold text-slate-900">
            Portfolio Forecasting
          </h2>
          <p className="text-slate-500 mt-1">
            Project future growth with hypothetical buy/sell scenarios
          </p>
        </div>
      </div>

      {/* Saved Scenarios Bar */}
      <Card className="border-[#547d91]/20 bg-[#547d91]/5" data-testid="saved-scenarios-bar">
        <CardContent className="py-4">
          <div className="flex flex-col lg:flex-row lg:items-center gap-3">
            <div className="flex items-center gap-2 text-slate-700 font-medium shrink-0">
              <FolderOpen className="w-5 h-5 text-[#547d91]" />
              Saved Scenarios
            </div>
            <div className="flex-1 min-w-0">
              <Select
                value={currentScenarioId || ""}
                onValueChange={(v) => loadScenario(v)}
              >
                <SelectTrigger className="bg-white" data-testid="scenario-load-select">
                  <SelectValue placeholder={savedScenarios.length ? "Load a saved scenario…" : "No saved scenarios yet"} />
                </SelectTrigger>
                <SelectContent>
                  {savedScenarios.map((s) => (
                    <SelectItem key={s.id} value={s.id} data-testid={`scenario-option-${s.id}`}>
                      {s.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {currentScenarioId && (
                <Button
                  variant="outline"
                  className="rounded-full"
                  onClick={() => openSaveDialog(false)}
                  data-testid="scenario-save-btn"
                >
                  <Save className="w-4 h-4 mr-2" /> Save
                </Button>
              )}
              {currentScenarioId && (
                <Button
                  variant="outline"
                  className="rounded-full"
                  onClick={handleDuplicateScenario}
                  disabled={duplicating}
                  data-testid="scenario-duplicate-btn"
                >
                  {duplicating ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Copying…</>) : (<><Copy className="w-4 h-4 mr-2" /> Duplicate</>)}
                </Button>
              )}
              <Button
                className="rounded-full bg-[#547d91] hover:bg-[#456a7a]"
                onClick={() => openSaveDialog(true)}
                data-testid="scenario-save-as-btn"
              >
                <Plus className="w-4 h-4 mr-2" /> Save as new
              </Button>
              {currentScenarioId && (
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button variant="ghost" size="icon" className="text-slate-400 hover:text-red-500" data-testid="scenario-delete-btn">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Delete this scenario?</AlertDialogTitle>
                      <AlertDialogDescription>
                        "{currentScenarioName}" will be permanently removed. This can't be undone.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel data-testid="scenario-delete-cancel">Cancel</AlertDialogCancel>
                      <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={handleDeleteScenario} data-testid="scenario-delete-confirm">
                        Delete
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              )}
            </div>
          </div>
          {currentScenarioName && (
            <p className="text-xs text-slate-500 mt-2" data-testid="active-scenario-label">
              Editing: <span className="font-medium text-[#547d91]">{currentScenarioName}</span> · changes aren't saved until you click Save
            </p>
          )}
        </CardContent>
      </Card>

      {/* Compare Scenarios */}
      {savedScenarios.length >= 2 && (
        <Card data-testid="compare-scenarios-card">
          <CardHeader>
            <CardTitle className="font-heading flex items-center gap-2">
              <GitCompareArrows className="w-5 h-5 text-[#547d91]" />
              Compare Scenarios
            </CardTitle>
            <CardDescription>
              Pick two saved scenarios to see which builds equity or value faster — and which is easier to hold on cashflow
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Scenario A</Label>
                <Select value={compareA} onValueChange={setCompareA}>
                  <SelectTrigger data-testid="compare-a-select"><SelectValue placeholder="Choose scenario A" /></SelectTrigger>
                  <SelectContent>
                    {savedScenarios.map((s) => (
                      <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Scenario B</Label>
                <Select value={compareB} onValueChange={setCompareB}>
                  <SelectTrigger data-testid="compare-b-select"><SelectValue placeholder="Choose scenario B" /></SelectTrigger>
                  <SelectContent>
                    {savedScenarios.map((s) => (
                      <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <Button
                className="rounded-full bg-[#547d91] hover:bg-[#456a7a]"
                onClick={runCompare}
                disabled={comparing}
                data-testid="compare-run-btn"
              >
                {comparing ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Comparing…</>) : (<><GitCompareArrows className="w-4 h-4 mr-2" /> Compare</>)}
              </Button>
              {compareData && (
                <div className="inline-flex rounded-full border border-slate-200 p-1" data-testid="compare-metric-toggle">
                  <button
                    className={`px-4 py-1 text-sm rounded-full transition-colors ${compareMetric === "equity" ? "bg-[#547d91] text-white" : "text-slate-600"}`}
                    onClick={() => setCompareMetric("equity")}
                    data-testid="compare-metric-equity"
                  >Equity</button>
                  <button
                    className={`px-4 py-1 text-sm rounded-full transition-colors ${compareMetric === "value" ? "bg-[#547d91] text-white" : "text-slate-600"}`}
                    onClick={() => setCompareMetric("value")}
                    data-testid="compare-metric-value"
                  >Value</button>
                  <button
                    className={`px-4 py-1 text-sm rounded-full transition-colors ${compareMetric === "cashflow" ? "bg-[#547d91] text-white" : "text-slate-600"}`}
                    onClick={() => setCompareMetric("cashflow")}
                    data-testid="compare-metric-cashflow"
                  >Cashflow</button>
                </div>
              )}
            </div>

            {compareData && compareMeta && (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="p-4 rounded-lg border border-[#547d91]/30 bg-[#547d91]/5" data-testid="compare-summary-a">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="w-3 h-3 rounded-full bg-[#547d91]" />
                      <span className="font-semibold text-slate-900">{compareMeta.a.name}</span>
                    </div>
                    <p className="text-sm text-slate-600">Final Value: <span className="font-semibold text-slate-900">{formatCurrency(compareMeta.a.value)}</span></p>
                    <p className="text-sm text-slate-600">Final Equity: <span className="font-semibold text-emerald-600">{formatCurrency(compareMeta.a.equity)}</span></p>
                    <p className="text-sm text-slate-600">Final Cashflow/yr: <span className={`font-semibold ${compareMeta.a.cashflow >= 0 ? "text-emerald-600" : "text-red-500"}`}>{formatCurrency(compareMeta.a.cashflow)}</span></p>
                  </div>
                  <div className="p-4 rounded-lg border border-amber-300/50 bg-amber-50" data-testid="compare-summary-b">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="w-3 h-3 rounded-full bg-amber-500" />
                      <span className="font-semibold text-slate-900">{compareMeta.b.name}</span>
                    </div>
                    <p className="text-sm text-slate-600">Final Value: <span className="font-semibold text-slate-900">{formatCurrency(compareMeta.b.value)}</span></p>
                    <p className="text-sm text-slate-600">Final Equity: <span className="font-semibold text-emerald-600">{formatCurrency(compareMeta.b.equity)}</span></p>
                    <p className="text-sm text-slate-600">Final Cashflow/yr: <span className={`font-semibold ${compareMeta.b.cashflow >= 0 ? "text-emerald-600" : "text-red-500"}`}>{formatCurrency(compareMeta.b.cashflow)}</span></p>
                  </div>
                </div>
                {(() => {
                  const av = compareMeta.a[compareMetric];
                  const bv = compareMeta.b[compareMetric];
                  const winner = av === bv ? null : (av > bv ? compareMeta.a : compareMeta.b);
                  const diff = Math.abs(av - bv);
                  const label = compareMetric === "cashflow" ? "annual cashflow" : compareMetric;
                  return winner ? (
                    <p className="text-sm text-center text-slate-700 bg-emerald-50 border border-emerald-200 rounded-lg py-2 px-3" data-testid="compare-winner">
                      <span className="font-semibold text-[#547d91]">{winner.name}</span> has <span className="font-semibold">{formatCurrency(diff)}</span> more {label} by the final year
                    </p>
                  ) : null;
                })()}
                <div className="h-80" data-testid="compare-chart">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={compareData} margin={{ top: 10, right: 20, left: 10, bottom: 10 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="year" label={{ value: 'Year', position: 'bottom', offset: -5 }} />
                      <YAxis tickFormatter={(v) => compareMetric === "cashflow" ? `$${Math.round(v / 1000)}k` : `$${(v / 1000000).toFixed(1)}M`} />
                      {compareMetric === "cashflow" && <ReferenceLine y={0} stroke="#94a3b8" strokeDasharray="4 4" />}
                      <Tooltip formatter={(v) => formatCurrency(v)} />
                      <Legend />
                      <Line
                        type="monotone"
                        dataKey={compareMetric === "equity" ? "a_equity" : compareMetric === "cashflow" ? "a_cashflow" : "a_value"}
                        name={compareMeta.a.name}
                        stroke="#547d91"
                        strokeWidth={2.5}
                        dot={false}
                      />
                      <Line
                        type="monotone"
                        dataKey={compareMetric === "equity" ? "b_equity" : compareMetric === "cashflow" ? "b_cashflow" : "b_value"}
                        name={compareMeta.b.name}
                        stroke="#f59e0b"
                        strokeWidth={2.5}
                        dot={false}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      )}



      {/* Settings Card */}
      <Card>
        <CardHeader>
          <CardTitle className="font-heading flex items-center gap-2">
            <Settings className="w-5 h-5 text-[#547d91]" />
            Forecast Settings
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div className="space-y-2">
              <Label>Growth Rate (%)</Label>
              <Input
                type="number"
                step="0.5"
                value={settings.growth_rate}
                onChange={(e) => setSettings({...settings, growth_rate: parseFloat(e.target.value)})}
                data-testid="growth-rate-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Rental Growth (%)</Label>
              <Input
                type="number"
                step="0.5"
                value={settings.rental_growth_rate}
                onChange={(e) => setSettings({...settings, rental_growth_rate: parseFloat(e.target.value)})}
                data-testid="rental-growth-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Expense Growth (%)</Label>
              <Input
                type="number"
                step="0.5"
                value={settings.expense_growth_rate}
                onChange={(e) => setSettings({...settings, expense_growth_rate: parseFloat(e.target.value)})}
                data-testid="expense-growth-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Interest Rate (%)</Label>
              <Input
                type="number"
                step="0.25"
                value={settings.interest_rate_forecast}
                onChange={(e) => setSettings({...settings, interest_rate_forecast: parseFloat(e.target.value)})}
                data-testid="interest-rate-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Years</Label>
              <Input
                type="number"
                min="1"
                max="30"
                value={settings.years}
                onChange={(e) => setSettings({...settings, years: parseInt(e.target.value)})}
                data-testid="years-input"
              />
            </div>
          </div>
          <div className="mt-6 pt-5 border-t border-slate-100">
            <p className="text-sm font-medium text-slate-700 mb-3">Home (PPOR) &amp; sale settings</p>
            <PporSummary property={properties.find(p => p.is_ppor)} />
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Selling costs (%)</Label>
                <Input type="number" step="0.1" value={settings.selling_costs_percent}
                  onChange={(e) => setSettings({...settings, selling_costs_percent: parseFloat(e.target.value) || 0})}
                  data-testid="selling-costs-input" />
              </div>
              <div className="space-y-2">
                <Label>Est. CGT on gain (%)</Label>
                <Input type="number" step="0.1" value={settings.cgt_estimate_percent}
                  onChange={(e) => setSettings({...settings, cgt_estimate_percent: parseFloat(e.target.value) || 0})}
                  data-testid="cgt-percent-input" />
              </div>
            </div>
            <p className="text-xs text-slate-400 mt-2">
              Your home is included in net worth (value &amp; loan) but excluded from rental cashflow. Selling costs and estimated CGT are deducted from sale proceeds.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Scenario Events */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="font-heading flex items-center gap-2">
              <Building2 className="w-5 h-5 text-[#547d91]" />
              Scenario Events
            </CardTitle>
            <CardDescription>
              Add hypothetical property purchases or sales to see their impact
            </CardDescription>
          </div>
          <Button 
            variant="outline" 
            className="rounded-full"
            onClick={() => setShowAddEventDialog(true)}
            data-testid="add-event-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Event
          </Button>
        </CardHeader>
        <CardContent>
          {events.length > 0 ? (
            <div className="space-y-3">
              {events.sort((a, b) => a.year - b.year).map((event) => (
                <div 
                  key={event.id}
                  className={`flex items-center justify-between p-4 rounded-lg border ${
                    event.event_type === "buy" 
                      ? "bg-emerald-50 border-emerald-200" 
                      : "bg-amber-50 border-amber-200"
                  }`}
                  data-testid={`event-${event.id}`}
                >
                  <div className="flex items-center gap-4">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      event.event_type === "buy" ? "bg-emerald-100" : "bg-amber-100"
                    }`}>
                      {event.event_type === "buy" ? (
                        <ArrowUpRight className="w-5 h-5 text-emerald-600" />
                      ) : (
                        <ArrowDownRight className="w-5 h-5 text-amber-600" />
                      )}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className={
                          event.event_type === "buy" ? "border-emerald-300 text-emerald-700" : "border-amber-300 text-amber-700"
                        }>
                          Year {event.year}
                        </Badge>
                        <span className="font-semibold text-slate-900 capitalize">
                          {event.event_type === "buy" ? "Purchase Property" : "Sell Property"}
                        </span>
                      </div>
                      <p className="text-sm text-slate-600 mt-1">
                        {event.event_type === "buy" ? (
                          <>
                            Value: {formatCurrency(parseFloat(event.property_value))} | 
                            Loan: {formatCurrency(parseFloat(event.loan_amount) || 0)} | 
                            Rent: {formatCurrency((parseFloat(event.weekly_rent) || 0) * 52)}/yr
                          </>
                        ) : (
                          <>
                            {properties.find(p => p.id === event.property_id)?.address || "Property"} 
                            {event.sale_price && ` at ${formatCurrency(parseFloat(event.sale_price))}`}
                            {(() => {
                              const tc = getTaxCategory(properties.find(p => p.id === event.property_id));
                              return tc ? (
                                <Badge variant="outline" className={`ml-2 text-[10px] ${tc.className}`} data-testid="forecast-tax-badge">
                                  {tc.label}
                                </Badge>
                              ) : null;
                            })()}
                          </>
                        )}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="text-slate-400 hover:text-[#547d91]"
                      onClick={() => editEvent(event)}
                      data-testid={`edit-event-${event.id}`}
                    >
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="text-slate-400 hover:text-red-500"
                      onClick={() => removeEvent(event.id)}
                      data-testid={`remove-event-${event.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-center text-slate-500 py-6">
              No scenario events added. Click "Add Event" to model buying or selling properties in the future.
            </p>
          )}
          
          {events.length > 0 && (
            <Button 
              className="w-full mt-4 bg-[#547d91] hover:bg-[#456a7a] rounded-full"
              onClick={() => runForecast(true)}
              disabled={isForecastLoading}
              data-testid="run-forecast-btn"
            >
              {isForecastLoading ? "Calculating..." : "Apply Scenario Events"}
            </Button>
          )}
        </CardContent>
      </Card>

      {/* Forecast Results */}
      {isForecastLoading ? (
        <Skeleton className="h-96" />
      ) : forecast?.forecast?.length > 0 ? (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card className="metric-card">
              <CardContent className="p-5">
                <p className="text-sm text-slate-500 mb-1">
                  Projected Value (Year {settings.years})
                </p>
                <p className="text-2xl font-bold text-[#547d91]">
                  {formatCurrency(finalYear?.total_value || 0)}
                </p>
              </CardContent>
            </Card>
            <Card className="metric-card">
              <CardContent className="p-5">
                <p className="text-sm text-slate-500 mb-1">
                  Projected Equity (Year {settings.years})
                </p>
                <p className="text-2xl font-bold text-emerald-600">
                  {formatCurrency(finalYear?.total_equity || 0)}
                </p>
              </CardContent>
            </Card>
            <Card className="metric-card">
              <CardContent className="p-5">
                <p className="text-sm text-slate-500 mb-1">
                  Projected LVR (Year {settings.years})
                </p>
                <p className="text-2xl font-bold text-slate-900">
                  {formatPercent(finalYear?.average_lvr || 0)}
                </p>
              </CardContent>
            </Card>
            <Card className="metric-card">
              <CardContent className="p-5">
                <p className="text-sm text-slate-500 mb-1">
                  Properties (Year {settings.years})
                </p>
                <p className="text-2xl font-bold text-slate-900">
                  {finalYear?.num_properties || properties.length}
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Main Chart */}
          <Card>
            <CardHeader>
              <CardTitle className="font-heading">Portfolio Value & Equity Growth</CardTitle>
              {events.length > 0 && (
                <CardDescription>
                  Vertical lines indicate buy/sell events
                </CardDescription>
              )}
            </CardHeader>
            <CardContent>
              <div className="h-96">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={forecast.forecast}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="year" label={{ value: 'Year', position: 'bottom', offset: -5 }} />
                    <YAxis tickFormatter={(v) => `$${(v/1000000).toFixed(1)}M`} />
                    <Tooltip 
                      formatter={(value, name) => [formatCurrency(value), name]}
                      contentStyle={{ borderRadius: "8px", border: "1px solid #e2e8f0" }}
                    />
                    <Legend />
                    {/* Reference lines for events */}
                    {events.map((event) => (
                      <ReferenceLine 
                        key={event.id}
                        x={event.year} 
                        stroke={event.event_type === "buy" ? "#10B981" : "#F59E0B"} 
                        strokeDasharray="5 5"
                        strokeWidth={2}
                      />
                    ))}
                    <Area 
                      type="monotone" 
                      dataKey="total_value" 
                      name="Total Value"
                      stroke="#547d91" 
                      fill="#547d91" 
                      fillOpacity={0.3}
                    />
                    <Area 
                      type="monotone" 
                      dataKey="total_equity" 
                      name="Total Equity"
                      stroke="#10B981" 
                      fill="#10B981"
                      fillOpacity={0.3}
                    />
                    <Area 
                      type="monotone" 
                      dataKey="total_debt" 
                      name="Total Debt"
                      stroke="#F59E0B" 
                      fill="#F59E0B"
                      fillOpacity={0.3}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Cashflow Chart */}
          <Card>
            <CardHeader>
              <CardTitle className="font-heading">Cashflow</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={forecast.forecast}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="year" />
                    <YAxis tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`} />
                    <Tooltip 
                      formatter={(value, name) => [formatCurrency(value), name]}
                      contentStyle={{ borderRadius: "8px", border: "1px solid #e2e8f0" }}
                    />
                    <Legend />
                    {events.map((event) => (
                      <ReferenceLine 
                        key={`line-${event.id}`}
                        x={event.year} 
                        stroke={event.event_type === "buy" ? "#10B981" : "#F59E0B"} 
                        strokeDasharray="5 5"
                      />
                    ))}
                    <Line 
                      type="monotone" 
                      dataKey="total_annual_rental" 
                      name="Annual Rent"
                      stroke="#10B981" 
                      strokeWidth={2}
                      dot={false}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="total_annual_expenses" 
                      name="Annual Expenses"
                      stroke="#EF4444" 
                      strokeWidth={2}
                      dot={false}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="net_cashflow" 
                      name="Net Income"
                      stroke="#547d91" 
                      strokeWidth={2}
                      dot={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </>
      ) : (
        <Card className="p-8 text-center">
          <p className="text-slate-500">Add properties to see forecast projections</p>
        </Card>
      )}

      {/* Add/Edit Event Dialog */}
      <Dialog open={showAddEventDialog} onOpenChange={closeEventDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingEvent ? "Edit Scenario Event" : "Add Scenario Event"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div className="space-y-2">
              <Label>Event Type</Label>
              <Select 
                value={newEvent.event_type} 
                onValueChange={(value) => setNewEvent({...newEvent, event_type: value})}
              >
                <SelectTrigger data-testid="event-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="buy">Buy New Property</SelectItem>
                  <SelectItem value="sell">Sell Existing Property</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Year (1-{settings.years})</Label>
              <Input
                type="number"
                min="1"
                max={settings.years}
                value={newEvent.year}
                onChange={(e) => setNewEvent({...newEvent, year: parseInt(e.target.value)})}
                data-testid="event-year-input"
              />
            </div>
            
            {newEvent.event_type === "buy" ? (
              <>
                <div className="space-y-2">
                  <Label>Property Value *</Label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">$</span>
                    <Input
                      type="number"
                      className="pl-7"
                      placeholder="800000"
                      value={newEvent.property_value}
                      onChange={(e) => setNewEvent({...newEvent, property_value: e.target.value})}
                      data-testid="event-value-input"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Loan Amount</Label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">$</span>
                    <Input
                      type="number"
                      className="pl-7"
                      placeholder="640000"
                      value={newEvent.loan_amount}
                      onChange={(e) => setNewEvent({...newEvent, loan_amount: e.target.value})}
                      data-testid="event-loan-input"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Weekly Rent</Label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">$</span>
                    <Input
                      type="number"
                      className="pl-7"
                      placeholder="600"
                      value={newEvent.weekly_rent}
                      onChange={(e) => setNewEvent({...newEvent, weekly_rent: e.target.value})}
                      data-testid="event-rent-input"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Interest Rate (%)</Label>
                    <Input
                      type="number"
                      step="0.25"
                      value={newEvent.interest_rate}
                      onChange={(e) => setNewEvent({...newEvent, interest_rate: e.target.value})}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Loan Term (years)</Label>
                    <Input
                      type="number"
                      value={newEvent.loan_term_years}
                      onChange={(e) => setNewEvent({...newEvent, loan_term_years: e.target.value})}
                    />
                  </div>
                </div>
                <LoanTypeFields value={newEvent} onChange={(patch) => setNewEvent(prev => ({ ...prev, ...patch }))} testIdPrefix="event-loan" />
              </>
            ) : (
              <>
                <div className="space-y-2">
                  <Label>Property to Sell *</Label>
                  <Select 
                    value={newEvent.property_id} 
                    onValueChange={(value) => setNewEvent({...newEvent, property_id: value})}
                  >
                    <SelectTrigger data-testid="event-property-select">
                      <SelectValue placeholder="Select property" />
                    </SelectTrigger>
                    <SelectContent>
                      {properties.map((property) => (
                        <SelectItem key={property.id} value={property.id}>
                          {property.address}, {property.suburb}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Sale Price (optional - defaults to projected value)</Label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">$</span>
                    <Input
                      type="number"
                      className="pl-7"
                      placeholder="Leave blank for projected value"
                      value={newEvent.sale_price}
                      onChange={(e) => setNewEvent({...newEvent, sale_price: e.target.value})}
                      data-testid="event-sale-price-input"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>What to do with the net proceeds?</Label>
                  <Select
                    value={newEvent.proceeds_action}
                    onValueChange={(v) => setNewEvent({...newEvent, proceeds_action: v})}
                  >
                    <SelectTrigger data-testid="event-proceeds-action">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="cash">Cash out (keep as cash)</SelectItem>
                      <SelectItem value="ppor">Pay down home (PPOR) loan</SelectItem>
                      <SelectItem value="investment">Pay down investment loans (highest rate first)</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-slate-400">
                    Net proceeds = sale price − selling costs ({settings.selling_costs_percent}%) − est. CGT − remaining loan.
                  </p>
                </div>
                {newEvent.proceeds_action === "ppor" && (
                  <div className="space-y-2">
                    <Label>After the home loan is cleared, apply any remainder to…</Label>
                    <Select
                      value={newEvent.ppor_remainder_action}
                      onValueChange={(v) => setNewEvent({...newEvent, ppor_remainder_action: v})}
                    >
                      <SelectTrigger data-testid="event-ppor-remainder-action">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="cash">Cash out</SelectItem>
                        <SelectItem value="investment">Pay down investment loans (highest rate first)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </>
            )}
            
            <Button 
              className="w-full bg-[#547d91] hover:bg-[#456a7a] rounded-full"
              onClick={addEvent}
              data-testid="save-event-btn"
            >
              {editingEvent ? "Save Changes" : "Add Event"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Save Scenario Dialog */}
      <Dialog open={showSaveDialog} onOpenChange={setShowSaveDialog}>
        <DialogContent data-testid="scenario-save-dialog">
          <DialogHeader>
            <DialogTitle>{saveAsNew ? "Save scenario" : "Update scenario"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <Label htmlFor="scenario-name">Scenario name</Label>
            <Input
              id="scenario-name"
              value={saveName}
              onChange={(e) => setSaveName(e.target.value)}
              placeholder="e.g. Buy a unit in year 3"
              data-testid="scenario-name-input"
              onKeyDown={(e) => { if (e.key === "Enter") handleSaveScenario(); }}
            />
            <p className="text-xs text-slate-500">
              Saves your current forecast settings and all {events.length} scenario event{events.length === 1 ? "" : "s"}.
            </p>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setShowSaveDialog(false)} data-testid="scenario-save-cancel">
              Cancel
            </Button>
            <Button
              className="bg-[#547d91] hover:bg-[#456a7a]"
              onClick={handleSaveScenario}
              disabled={savingScenario}
              data-testid="scenario-save-confirm"
            >
              {savingScenario ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving…</>) : "Save"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

    </div>
  );
}

const PporSummary = ({ property }) => (
  <div className="mb-4 p-3 rounded-lg bg-slate-50 border border-slate-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2" data-testid="ppor-summary">
    {property ? (
      <>
        <p className="text-sm text-slate-700">
          <span className="font-medium">Your home:</span> {property.address}, {property.suburb} — value {formatCurrency(property.current_value)}, loan {formatCurrency(property.loan_amount)} at {property.interest_rate}%
        </p>
        <Link to={`/properties/${property.id}/edit`} className="text-sm text-[#547d91] hover:underline shrink-0" data-testid="ppor-edit-link">Edit home details</Link>
      </>
    ) : (
      <>
        <p className="text-sm text-slate-600">No property is marked as your home (PPOR). Turn on "Principal Place of Residence" on a property to include it here.</p>
        <Link to="/properties" className="text-sm text-[#547d91] hover:underline shrink-0" data-testid="ppor-set-link">Go to Properties</Link>
      </>
    )}
  </div>
);
